export type ApiErrorBody = {
  code?: number;
  message?: string;
};

/**
 * Read the API error body off an Axios-like error.
 *
 * Intentionally duck-typed instead of `error instanceof AxiosError`: the error
 * can be thrown by an axios instance that isn't the exact same module instance
 * (bundled/duplicate axios), which makes `instanceof` silently return `false`
 * and hides the `code`/`message` we need for retry + messaging.
 */
const getApiErrorBody = (error: unknown): ApiErrorBody | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  const response = (error as { response?: { data?: unknown } }).response;

  if (typeof response?.data !== 'object' || response.data === null) {
    return undefined;
  }

  return response.data as ApiErrorBody;
};

/** Returns the API `message` from an Axios error body when present, otherwise `fallback`. */
export const getApiErrorMessage = (error: unknown, fallback?: string): string => {
  const message = getApiErrorBody(error)?.message?.trim();

  return message || (fallback ?? '');
};

/** Returns the API error `code` from an Axios error body, or `undefined` when absent. */
export const getApiErrorCode = (error: unknown): number | undefined => getApiErrorBody(error)?.code;

/** Returns the HTTP status from an Axios-like error, or `undefined` when absent. */
export const getApiErrorStatus = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  const status = (error as { response?: { status?: unknown } }).response?.status;

  return typeof status === 'number' ? status : undefined;
};

/** `true` when the request failed with HTTP 401 (unauthenticated / expired session). */
export const isUnauthorizedError = (error: unknown): boolean => getApiErrorStatus(error) === 401;

/**
 * Parse the server's actual aggregate version out of an optimistic-lock error
 * message (e.g. "...Requested version [267], but actual version is [268]...").
 * Works for any versioned aggregate (cart, favorites). Lets us replay the
 * mutation against the real version without depending on a refetch.
 */
export const getActualVersionFromError = (error: unknown): number | undefined => {
  const message = getApiErrorBody(error)?.message;
  const match = message?.match(/actual version is \[(\d+)\]/i);

  return match ? Number(match[1]) : undefined;
};

/**
 * Backend error code for an optimistic-lock failure: the cart `version` we sent
 * is behind the server's actual version (a concurrent mutation got there first).
 * Recoverable by reconciling to the real version and retrying.
 */
export const OPTIMISTIC_LOCK_ERROR_CODE = 3;

export const isOptimisticLockError = (error: unknown): boolean =>
  getApiErrorCode(error) === OPTIMISTIC_LOCK_ERROR_CODE;

/**
 * Backend error code for a no-op command: the mutation wouldn't change anything
 * (e.g. setting the same quantity). Harmless — safe to swallow without notifying.
 */
export const NO_CHANGES_ERROR_CODE = 1004;

export const isNoChangesError = (error: unknown): boolean =>
  getApiErrorCode(error) === NO_CHANGES_ERROR_CODE;
